<template>
  <div class="flex flex-col h-full bg-gray-900">
    <template v-if="selectedTMDBTitle">
      <!-- Drag handle at top (hidden when inside Sheet which provides its own) -->
      <div v-if="showDragHandle" class="flex justify-center pt-3 pb-1 shrink-0">
        <div class="w-10 h-1 rounded-full bg-white/30" />
      </div>

      <!-- Header with title strip (fixed/shrink-0) -->
      <div class="shrink-0 px-4 pb-3">
        <div class="border border-gray-700 rounded-lg overflow-hidden">
          <TitleStrip
            :title="selectedTMDBTitle"
            :condensed="true"
            :dark="true" />
        </div>
      </div>

      <!-- Draft resumed notice -->
      <div
        v-if="didResumeDraft"
        class="shrink-0 px-4 pb-2">
        <div class="flex items-center justify-between">
          <p class="text-xs text-gray-500">
            Continuing draft
          </p>
          <button
            type="button"
            class="text-xs text-gray-500 underline underline-offset-2 hover:text-gray-300 transition-colors"
            @click="handleStartFresh">
            Start fresh
          </button>
        </div>
      </div>

      <!-- Scrollable middle section -->
      <div class="flex-1 overflow-y-auto min-h-0 px-4">
        <!-- Step Indicator -->
        <div class="flex items-center justify-center gap-2 mb-4">
        <button
          type="button"
          class="flex items-center gap-1.5 px-3 py-1 text-xs font-medium rounded-full transition-colors"
          :class="
            currentStep === 1
              ? 'bg-indigo-500 text-white'
              : 'bg-gray-800 text-gray-400'
          "
          @click="currentStep = 1">
          <span
            class="w-4 h-4 flex items-center justify-center rounded-full text-[10px] font-bold"
            :class="
              currentStep === 1
                ? 'bg-white/20'
                : 'bg-gray-700'
            ">1</span>
          Scores
        </button>
        <div class="w-6 h-px bg-gray-700"></div>
        <button
          type="button"
          class="flex items-center gap-1.5 px-3 py-1 text-xs font-medium rounded-full transition-colors"
          :class="
            currentStep === 2
              ? 'bg-indigo-500 text-white'
              : 'bg-gray-800 text-gray-400'
          "
          @click="currentStep = 2">
          <span
            class="w-4 h-4 flex items-center justify-center rounded-full text-[10px] font-bold"
            :class="
              currentStep === 2
                ? 'bg-white/20'
                : 'bg-gray-700'
            ">2</span>
          Details
        </button>
        <div class="w-6 h-px bg-gray-700"></div>
        <button
          type="button"
          class="flex items-center gap-1.5 px-3 py-1 text-xs font-medium rounded-full transition-colors"
          :class="
            currentStep === 3
              ? 'bg-indigo-500 text-white'
              : 'bg-gray-800 text-gray-400'
          "
          @click="currentStep = 3">
          <span
            class="w-4 h-4 flex items-center justify-center rounded-full text-[10px] font-bold"
            :class="
              currentStep === 3
                ? 'bg-white/20'
                : 'bg-gray-700'
            ">3</span>
          Image
        </button>
        </div>

        <!-- Step 1: Scores Only -->
        <div
          v-show="currentStep === 1"
          :class="sliderMode === 'vertical' ? 'flex flex-col h-full gap-4' : 'space-y-4'">
          <template v-if="isTitleTv">
            <FormTVWatchSelector
              v-model:review-type="review.review_type"
              v-model:tv-season="review.tv_season"
              v-model:tv-episode="review.tv_episode"
              v-model:title-subtitle="review.title_subtitle"
              :can-modify="true"
              :title="selectedTMDBTitle" />
          </template>

          <!-- Slider Inputs: Horizontal (default) -->
          <div v-if="sliderMode !== 'vertical'">
            <FormSliderInput
              :label="'Story'"
              :model="review.score_a"
              :expanded="expandedSlider === 'Story'"
              :dial-enabled="sliderDialEnabled"
              @update:model="onSliderUpdate('score_a', $event)"
              @maxed="checkForPerfectScore"
              @zeroed="checkForZeroScore"
              @request-expand="expandedSlider = 'Story'"
              @request-collapse="expandedSlider = null" />
            <FormSliderInput
              :label="'Characters'"
              :model="review.score_b"
              :expanded="expandedSlider === 'Characters'"
              :dial-enabled="sliderDialEnabled"
              @update:model="onSliderUpdate('score_b', $event)"
              @maxed="checkForPerfectScore"
              @zeroed="checkForZeroScore"
              @request-expand="expandedSlider = 'Characters'"
              @request-collapse="expandedSlider = null" />
            <FormSliderInput
              :label="'Execution'"
              :model="review.score_c"
              :expanded="expandedSlider === 'Execution'"
              :dial-enabled="sliderDialEnabled"
              @update:model="onSliderUpdate('score_c', $event)"
              @maxed="checkForPerfectScore"
              @zeroed="checkForZeroScore"
              @request-expand="expandedSlider = 'Execution'"
              @request-collapse="expandedSlider = null" />
            <FormSliderInput
              :label="'Entertainment'"
              :model="review.score_d"
              :expanded="expandedSlider === 'Entertainment'"
              :dial-enabled="sliderDialEnabled"
              @update:model="onSliderUpdate('score_d', $event)"
              @maxed="checkForPerfectScore"
              @zeroed="checkForZeroScore"
              @request-expand="expandedSlider = 'Entertainment'"
              @request-collapse="expandedSlider = null" />
            <FormSliderInput
              :label="'Impact'"
              :model="review.score_e"
              :expanded="expandedSlider === 'Impact'"
              :dial-enabled="sliderDialEnabled"
              @update:model="onSliderUpdate('score_e', $event)"
              @maxed="checkForPerfectScore"
              @zeroed="checkForZeroScore"
              @request-expand="expandedSlider = 'Impact'"
              @request-collapse="expandedSlider = null" />
            <FormSliderInput
              :label="'Universe'"
              :model="review.score_f"
              :expanded="expandedSlider === 'Universe'"
              :dial-enabled="sliderDialEnabled"
              @update:model="onSliderUpdate('score_f', $event)"
              @maxed="checkForPerfectScore"
              @zeroed="checkForZeroScore"
              @request-expand="expandedSlider = 'Universe'"
              @request-collapse="expandedSlider = null" />
          </div>

          <!-- Slider Inputs: Vertical faders (mixing desk) -->
          <div
            v-else
            class="flex gap-1.5 min-h-0 flex-1"
            style="touch-action: none"
            @touchmove.stop.prevent>
            <FormSliderVertical
              :label="'Story'"
              :model="review.score_a"
              :expanded="expandedSlider === 'Story'"
              :dial-enabled="sliderDialEnabled"
              @update:model="onSliderUpdate('score_a', $event)"
              @request-expand="expandedSlider = 'Story'"
              @request-collapse="expandedSlider = null" />
            <FormSliderVertical
              :label="'Characters'"
              :model="review.score_b"
              :expanded="expandedSlider === 'Characters'"
              :dial-enabled="sliderDialEnabled"
              @update:model="onSliderUpdate('score_b', $event)"
              @request-expand="expandedSlider = 'Characters'"
              @request-collapse="expandedSlider = null" />
            <FormSliderVertical
              :label="'Execution'"
              :model="review.score_c"
              :expanded="expandedSlider === 'Execution'"
              :dial-enabled="sliderDialEnabled"
              @update:model="onSliderUpdate('score_c', $event)"
              @request-expand="expandedSlider = 'Execution'"
              @request-collapse="expandedSlider = null" />
            <FormSliderVertical
              :label="'Entertainment'"
              :model="review.score_d"
              :expanded="expandedSlider === 'Entertainment'"
              :dial-enabled="sliderDialEnabled"
              @update:model="onSliderUpdate('score_d', $event)"
              @request-expand="expandedSlider = 'Entertainment'"
              @request-collapse="expandedSlider = null" />
            <FormSliderVertical
              :label="'Impact'"
              :model="review.score_e"
              :expanded="expandedSlider === 'Impact'"
              :dial-enabled="sliderDialEnabled"
              @update:model="onSliderUpdate('score_e', $event)"
              @request-expand="expandedSlider = 'Impact'"
              @request-collapse="expandedSlider = null" />
            <FormSliderVertical
              :label="'Universe'"
              :model="review.score_f"
              :expanded="expandedSlider === 'Universe'"
              :dial-enabled="sliderDialEnabled"
              @update:model="onSliderUpdate('score_f', $event)"
              @request-expand="expandedSlider = 'Universe'"
              @request-collapse="expandedSlider = null" />
          </div>

          <!-- Expanded dial for vertical fader -->
          <div
            v-if="expandedSlider && sliderMode === 'vertical' && sliderDialEnabled"
            class="flex flex-col items-center pt-2 shrink-0">
            <div class="flex items-center gap-2 w-full mb-2 shrink-0">
              <p class="text-xs text-gray-400 flex-1">{{ expandedSliderDescription }}</p>
              <button
                class="flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg transition-colors"
                :class="expandedSliderTextIsDark ? 'text-gray-900' : 'text-white'"
                :style="{ backgroundColor: expandedSliderColor }"
                @click="expandedSlider = null">
                <fa-icon icon="check" class="text-[10px]" />
                Done
              </button>
            </div>
            <div class="flex-1 flex items-center justify-center w-full min-h-0">
              <FormSliderDial
                :value="expandedSliderValue"
                :color="expandedSliderColor"
                :label="expandedSlider"
                :dial-size="240"
                @update:value="onExpandedDialUpdate" />
            </div>
          </div>
        </div>

        <!-- Step 2: Details/Meta Information -->
        <div v-show="currentStep === 2" class="space-y-4">
          <ReviewMetaEditable
            v-model:sentiment="review.sentiment"
            v-model:tags="review.tags"
            v-model:watched-on="review.watched_on"
            v-model:when="review.when"
            v-model:highlight="review.highlight"
            v-model:lowlight="review.lowlight"
            v-model:note="review.note"
            v-model:club-id="review.club_id"
            :sentiment-options="sentimentOptions"
            :title="selectedTMDBTitle" />
        </div>

        <!-- Step 3: Image/Canvas Editor -->
        <div v-show="currentStep === 3" class="space-y-4">
          <!-- Cover Image Selection -->
          <ReviewImageEditor
            ref="imageEditorRef"
            :images="props.title?.images?.backdrops || []"
            :initial-index="currentIndex"
            :score-color="scoreColorComputed"
            :title-text="titleTextComputed"
            :username="currentUsername"
            :score="scoreOverallComputed"
            :year="titleYearComputed"
            :initial-transform="existingTransform"
            :initial-image-path="existingImagePath"
            @update:image-path="onImagePathUpdate" />
        </div>
      </div>

      <!-- Footer: Navigation/Submit Buttons (fixed at bottom) -->
      <div
        class="shrink-0 px-4 pt-4 border-t border-white/10 bg-gray-900"
        style="padding-bottom: max(env(safe-area-inset-bottom), 16px)">
        <!-- Step 1: Next Button -->
        <template v-if="currentStep === 1">
          <button
            type="button"
            class="flex w-full justify-center items-center py-3 px-4 text-sm font-semibold rounded-lg border border-transparent transition-all duration-300 select-none"
            :class="[
              hasInteracted
                ? ''
                : 'bg-linear-to-r from-sky-500 to-indigo-500 hover:from-sky-400 hover:to-indigo-400',
              hasInteracted && buttonTextIsDark ? 'text-gray-900' : 'text-white'
            ]"
            :style="
              hasInteracted ? { backgroundColor: scoreColorComputed } : null
            "
            @click.prevent="goToStep2">
            Next <fa-icon icon="arrow-right" class="ml-2" />
          </button>
        </template>

        <!-- Step 2: Back + Next Buttons -->
        <template v-else-if="currentStep === 2">
          <div class="flex gap-2">
            <button
              type="button"
              class="flex-1 flex justify-center items-center py-3 px-4 text-sm font-medium rounded-lg bg-gray-800 text-gray-300 hover:bg-gray-700 transition-colors"
              @click.prevent="goToStep1">
              <fa-icon icon="arrow-left" class="mr-2" /> Back
            </button>
            <button
              type="button"
              class="flex-[2] flex justify-center items-center py-3 px-4 text-sm font-semibold rounded-lg border border-transparent transition-all duration-300 select-none"
              :class="[
                hasInteracted
                  ? ''
                  : 'bg-linear-to-r from-sky-500 to-indigo-500 hover:from-sky-400 hover:to-indigo-400',
                hasInteracted && buttonTextIsDark ? 'text-gray-900' : 'text-white'
              ]"
              :style="
                hasInteracted ? { backgroundColor: scoreColorComputed } : null
              "
              @click.prevent="goToStep3">
              Next <fa-icon icon="arrow-right" class="ml-2" />
            </button>
          </div>
        </template>

        <!-- Step 3: Back + Submit Buttons -->
        <template v-else>
          <div class="flex gap-2">
            <button
              type="button"
              class="flex-1 flex justify-center items-center py-3 px-4 text-sm font-medium rounded-lg bg-gray-800 text-gray-300 hover:bg-gray-700 transition-colors"
              @click.prevent="goToStep2">
              <fa-icon icon="arrow-left" class="mr-2" /> Back
            </button>
            <button
              type="button"
              :disabled="formBusy"
              class="flex-[2] flex justify-center items-center py-3 px-4 text-sm font-semibold rounded-lg border border-transparent transition-all duration-300 disabled:pointer-events-none disabled:opacity-70 select-none"
              :class="[
                hasInteracted
                  ? ''
                  : 'bg-linear-to-r from-sky-500 to-indigo-500 hover:from-sky-400 hover:to-indigo-400',
                hasInteracted && buttonTextIsDark
                  ? 'text-gray-900'
                  : 'text-white',
                formBusy ? 'animate-pulse' : ''
              ]"
              :style="
                hasInteracted ? { backgroundColor: scoreColorComputed } : null
              "
              @click.prevent="handleSubmit">
              <template v-if="!inEditMode">
                <span v-if="formBusy" class="flex items-center">
                  <svg
                    class="mr-2 -ml-1 w-4 h-4 animate-spin"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24">
                    <circle
                      class="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      stroke-width="4"></circle>
                    <path
                      class="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Saving...
                </span>
                <span v-else>Create <fa-icon icon="check" class="ml-2" /></span>
              </template>
              <template v-else>
                <span v-if="formBusy" class="flex items-center">
                  <svg
                    class="mr-2 -ml-1 w-4 h-4 animate-spin"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24">
                    <circle
                      class="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      stroke-width="4"></circle>
                    <path
                      class="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Updating...
                </span>
                <span v-else>Update <fa-icon icon="check" class="ml-2" /></span>
              </template>
            </button>
          </div>
        </template>

        <div
          v-if="formError"
          class="p-2 mt-2 text-xs rounded-sm cursor-pointer bg-red-500/80 hover:bg-red-600"
          @click.prevent="clearFormError">
          Error saving review: {{ formErrorMessage }}
        </div>
      </div>
    </template>
    <div v-else class="flex flex-1 justify-center items-center">
      Loading details...
    </div>
  </div>
</template>

<script setup>
import { watch, computed, ref, onMounted, onBeforeUnmount } from 'vue'
import { useRouter } from 'vue-router'
import { useReviewForm } from '@/use/useReviewForm.js'
import FormSliderInput from '@/components/Pure/FormSliderInput'
import FormSliderVertical from '@/components/Pure/FormSliderVertical.vue'
import FormSliderDial from '@/components/Pure/FormSliderDial.vue'
import { useReviewColors } from '@/use/useReviewColors'
import { useHapticFeedback } from '@/composables/useHapticFeedback'
import { useUIPreferences } from '@/composables/useUIPreferences'
import confetti from 'canvas-confetti'
import JSConfetti from 'js-confetti'

import ReviewCard from '@/components/ReviewCard'
import { useStoreTMDB } from '@/stores/storeTMDB'
import { useStoreModals } from '@/stores/storeModals'
import { useStoreReviews } from '@/stores/storeReviews'
import { useStoreProfile } from '@/stores/storeProfile'
import { useStoreAuth } from '@/stores/storeAuth'
import FormTVWatchSelector from '@/components/Reviews/FormTVWatchSelector'
import ReviewImageEditor from '@/components/Reviews/ReviewImageEditor.vue'
import ReviewMetaEditable from '@/components/Reviews/ReviewMetaEditable.vue'
import TitleStrip from '@/components/TitleStrip.vue'

const router = useRouter()
const storeModals = useStoreModals()
const storeTMDB = useStoreTMDB()
const storeReviews = useStoreReviews()
const storeProfile = useStoreProfile()
const storeAuth = useStoreAuth()
const {
  handleSave,
  review,
  selectTitleForReview,
  selectedTMDBTitle,
  sentimentOptions,
  setReviewForEdit,
  setReviewForNew,
  createDraft,
  saveDraftProgress,
  draftId,
  checkForExistingDraft,
  discardAndRestart,
  didResumeDraft
} = useReviewForm()
const { scoreHsl } = useReviewColors()
const haptics = useHapticFeedback()
const { sliderMode, sliderDialEnabled } = useUIPreferences()

const expandedSlider = ref(null) // Track which slider is expanded (by label)

// Score field mapping for expanded vertical fader dial
const SCORE_FIELD_MAP = {
  Story: 'score_a',
  Characters: 'score_b',
  Execution: 'score_c',
  Entertainment: 'score_d',
  Impact: 'score_e',
  Universe: 'score_f'
}

const INFO_DESCRIPTIONS = {
  Story: 'Narrative structure and depth. Low end: Predictable, Shallow. High end: Intriguing, Complex.',
  Characters: 'Character development and portrayal. Low end: Flat, Stereotypical. High end: Multidimensional, Compelling.',
  Execution: 'Technical quality and direction. Low end: Sloppy, Unpolished. High end: Masterful, Sleek.',
  Entertainment: 'Enjoyment and fun factor. Low end: Dull, Tedious. High end: Engaging, Thrilling.',
  Impact: 'Emotional and societal resonance. Low end: Superficial, Forgettable. High end: Profound, Memorable.',
  Universe: 'Setting and universe creation. Low end: Inconsistent, Sparse. High end: Rich, Believable.'
}

const expandedSliderValue = computed(() => {
  if (!expandedSlider.value) return 5
  const field = SCORE_FIELD_MAP[expandedSlider.value]
  return field ? review[field] : 5
})

const expandedSliderColor = computed(() => {
  return scoreHsl(expandedSliderValue.value)
})

const expandedSliderDescription = computed(() => {
  return INFO_DESCRIPTIONS[expandedSlider.value] || ''
})

const expandedSliderTextIsDark = computed(() => {
  return expandedSliderValue.value >= 6
})

const onExpandedDialUpdate = (value) => {
  if (!expandedSlider.value) return
  const field = SCORE_FIELD_MAP[expandedSlider.value]
  if (field) onSliderUpdate(field, value)
}

const formBusy = ref(false)
const formError = ref(null)
const formErrorMessage = ref(null)
const currentIndex = ref(0) // For tracking the current image index
const selectedImage = ref(null) // Selected image
const imageEditorRef = ref(null) // Reference to image editor component
const hasInteracted = ref(false) // Track if user has interacted with sliders
const createdReview = ref(null) // Store the created review for success sheet
const hasTriggeredPerfectScore = ref(false) // Track if we've triggered the all-10s celebration
const hasTriggeredZeroScore = ref(false) // Track if we've triggered the all-0s effect
let jsConfettiInstance = null // JSConfetti for emoji effects
const currentStep = ref(1) // Step 1: Scores + Meta, Step 2: Image/Canvas
const isEditing = computed(() => {
  return review.id ? true : false
})
const totalImages = computed(() => {
  return props.title?.images?.backdrops?.length || 0
})

const props = defineProps({
  title: Object,
  reviewType: [String],
  tvSeason: [Number],
  tvEpisode: [Number],
  existingReview: [Object],
  showDragHandle: {
    type: Boolean,
    default: true
  }
})

const emit = defineEmits({
  close: null
})

const titleComputed = computed(() => {
  if (props.title !== null && props.title !== undefined) return props.title

  if (props.existingReview) {
    return storeTMDB.fetchTitleById(
      props.existingReview.tmdb_id,
      props.existingReview.tmdb_type
    )
  }
  return false
})

const scoreOverallComputed = computed(() => {
  const overall =
    (review.score_a +
      review.score_b +
      review.score_c +
      review.score_d +
      review.score_e +
      review.score_f) /
    6

  return overall.toFixed(1)
})

// Compute score color for image editor
const scoreColorComputed = computed(() => {
  return scoreHsl(parseFloat(scoreOverallComputed.value))
})

// Emoji to show in success state based on score
const successEmoji = computed(() => {
  const score = parseFloat(scoreOverallComputed.value)
  if (score >= 9) return '🎉'
  if (score >= 7) return '🙂'
  if (score >= 5) return '😐'
  if (score >= 3) return '😕'
  return '😢'
})

// Determine if button text should be dark based on score color luminance
// Higher scores (green/yellow) are brighter and need dark text
const buttonTextIsDark = computed(() => {
  const score = parseFloat(scoreOverallComputed.value)
  // Scores above ~6 tend to be bright yellows/greens that need dark text
  return score >= 6
})

// Get current scores object for reaction component
const currentScores = computed(() => ({
  score_a: review.score_a,
  score_b: review.score_b,
  score_c: review.score_c,
  score_d: review.score_d,
  score_e: review.score_e,
  score_f: review.score_f
}))

// Get current user's username for text overlay
const currentUsername = computed(() => {
  const userId = storeAuth.user?.id
  if (!userId) return ''
  const profile = storeProfile.getProfile(userId)
  return profile?.username || ''
})

// Get title text for the review cover image
const titleTextComputed = computed(() => {
  return selectedTMDBTitle.value?.title || selectedTMDBTitle.value?.name || ''
})

// Get release year for the title
const titleYearComputed = computed(() => {
  const releaseDate =
    selectedTMDBTitle.value?.release_date ||
    selectedTMDBTitle.value?.first_air_date
  if (!releaseDate) return null
  return new Date(releaseDate).getFullYear()
})

const inEditMode = computed(() => {
  return props.existingReview ? true : false
})

// Get existing transform data for editing
const existingTransform = computed(() => {
  if (!props.existingReview?.image_transform) return null
  return props.existingReview.image_transform
})

// Get existing image path for editing
const existingImagePath = computed(() => {
  if (!props.existingReview?.image_path) return null
  return props.existingReview.image_path
})

watch(
  titleComputed,
  () => {
    if (titleComputed.value) {
      selectTitleForReview(titleComputed.value)
    }
  },
  { immediate: true }
)

// Step navigation functions
const goToStep1 = () => {
  haptics.light()
  currentStep.value = 1
}

const goToStep2 = () => {
  haptics.light()
  currentStep.value = 2
}

const goToStep3 = () => {
  haptics.light()
  currentStep.value = 3
}

const selectImage = () => {
  selectedImage.value =
    props.title.images.backdrops[currentIndex.value].file_path
  review.image_path = selectedImage.value
}

const prevImage = () => {
  if (currentIndex.value > 0) {
    currentIndex.value--
    selectImage()
  }
}

const nextImage = () => {
  if (currentIndex.value < props.title.images.backdrops.length - 1) {
    currentIndex.value++
    selectImage()
  }
}

// Handler for image path updates from the editor
const onImagePathUpdate = (imagePath) => {
  review.image_path = imagePath
  selectedImage.value = imagePath
}

// Handler for slider updates - sets hasInteracted and updates the score
const onSliderUpdate = (scoreKey, value) => {
  hasInteracted.value = true
  review[scoreKey] = value
}

// Check if all scores are at max (10) - trigger big celebration
const checkForPerfectScore = () => {
  const allTens =
    review.score_a === 10 &&
    review.score_b === 10 &&
    review.score_c === 10 &&
    review.score_d === 10 &&
    review.score_e === 10 &&
    review.score_f === 10

  if (allTens && !hasTriggeredPerfectScore.value) {
    hasTriggeredPerfectScore.value = true
    triggerPerfectScoreCelebration()
  } else if (!allTens) {
    hasTriggeredPerfectScore.value = false
  }
}

// Check if all scores are at min (0) - trigger big sad effect
const checkForZeroScore = () => {
  const allZeros =
    review.score_a === 0 &&
    review.score_b === 0 &&
    review.score_c === 0 &&
    review.score_d === 0 &&
    review.score_e === 0 &&
    review.score_f === 0

  if (allZeros && !hasTriggeredZeroScore.value) {
    hasTriggeredZeroScore.value = true
    triggerZeroScoreEffect()
  } else if (!allZeros) {
    hasTriggeredZeroScore.value = false
  }
}

// Big celebration for perfect 10 across all scores
const triggerPerfectScoreCelebration = () => {
  // Initialize JSConfetti if needed
  if (!jsConfettiInstance) {
    jsConfettiInstance = new JSConfetti()
  }

  // Party emoji explosion
  jsConfettiInstance.addConfetti({
    emojis: ['🎉', '🏆', '⭐', '🌟', '💯', '🎊', '👑', '🔥'],
    emojiSize: 40,
    confettiNumber: 40
  })

  // Also do some regular confetti from multiple angles
  const duration = 3000
  const end = Date.now() + duration

  const frame = () => {
    confetti({
      particleCount: 5,
      angle: 60,
      spread: 80,
      origin: { x: 0, y: 0.7 },
      colors: [
        '#ffd700',
        '#ff6b6b',
        '#4ecdc4',
        '#45b7d1',
        '#a29bfe',
        '#fd79a8'
      ],
      disableForReducedMotion: true
    })
    confetti({
      particleCount: 5,
      angle: 120,
      spread: 80,
      origin: { x: 1, y: 0.7 },
      colors: [
        '#ffd700',
        '#ff6b6b',
        '#4ecdc4',
        '#45b7d1',
        '#a29bfe',
        '#fd79a8'
      ],
      disableForReducedMotion: true
    })

    if (Date.now() < end) {
      requestAnimationFrame(frame)
    }
  }

  frame()
}

// Sad effect for all zeros
const triggerZeroScoreEffect = () => {
  // Initialize JSConfetti if needed
  if (!jsConfettiInstance) {
    jsConfettiInstance = new JSConfetti()
  }

  // Sad emoji shower
  jsConfettiInstance.addConfetti({
    emojis: ['😭', '💔', '😢', '😿', '🥀', '☔', '🌧️', '💧'],
    emojiSize: 40,
    confettiNumber: 50
  })
}

const isTitleTv = computed(() => {
  return props.title?.media_type === 'tv'
})

watch(
  () => props.title,
  async (newVal) => {
    if (newVal) {
      selectTitleForReview(
        newVal,
        props.reviewType,
        props.tvSeason,
        props.tvEpisode
      )
      selectedImage.value = newVal.images.backdrops[0]?.file_path || null

      // If title arrived after mount (async), check for existing draft first
      if (!props.existingReview && !draftId.value) {
        const existing = await checkForExistingDraft(
          newVal.id,
          newVal.media_type
        )
        if (existing) {
          // Draft was auto-resumed — show score colors
          hasInteracted.value = true
        } else {
          createDraft()
        }
      }
    }
  },
  { immediate: true }
)

// Watch for changes in TV-related props and re-trigger selectTitleForReview
watch(
  [() => props.reviewType, () => props.tvSeason, () => props.tvEpisode],
  () => {
    if (props.title) {
      selectTitleForReview(
        props.title,
        props.reviewType,
        props.tvSeason,
        props.tvEpisode
      )
    }
  }
)

// Trigger celebration or sad confetti based on score
const triggerCelebration = () => {
  const score = parseFloat(scoreOverallComputed.value)

  // For low scores (2 or below), show sad confetti with inverted probability
  // At 0: 100% chance, at 2: ~20% chance
  if (score <= 2) {
    const sadChance = 1 - score / 2.5 // 0 = 100%, 2 = 20%
    if (Math.random() < sadChance) {
      triggerSadSuccessEffect()
    }
    return
  }

  // For higher scores, show celebration with probability based on score
  // At 10: 100% chance, at 3: ~10% chance
  const celebrationChance = (score - 2) / 8 // Maps 3-10 to ~0.125-1.0
  if (Math.random() < celebrationChance) {
    triggerHappySuccessEffect()
  }
}

// Happy confetti for good scores
const triggerHappySuccessEffect = () => {
  // Single burst from center, toned down
  confetti({
    particleCount: 80,
    spread: 70,
    origin: { x: 0.5, y: 0.5 },
    colors: ['#ffd700', '#ff6b6b', '#4ecdc4', '#45b7d1', '#96ceb4', '#ffeaa7'],
    disableForReducedMotion: true
  })
}

// Sad confetti for low scores
const triggerSadSuccessEffect = () => {
  if (!jsConfettiInstance) {
    jsConfettiInstance = new JSConfetti()
  }

  jsConfettiInstance.addConfetti({
    emojis: ['😢', '😭', '💔', '😿'],
    emojiSize: 35,
    confettiNumber: 25
  })
}

const handleSubmit = async () => {
  // Prevent submission if already submitting
  if (formBusy.value) return

  haptics.medium()
  formBusy.value = true
  formError.value = null
  formErrorMessage.value = null

  try {
    // Get transform data before saving (instant — just reads current values)
    // Capture editor ref now — Vue nulls template refs on unmount after navigation
    let transformData = null
    const editor = imageEditorRef.value

    if (editor) {
      transformData = editor.getCurrentTransform()
      review.image_transform = transformData
    }

    // Fallback to default image selection if no editor
    if (!review.image_path) {
      selectImage()
    }

    // Save the review first to get the review ID
    const data = await handleSave()
    const reviewId = data.review.id

    // Force refresh the review cache to ensure we have the latest data
    // This is especially important after edits to ensure the UI shows updated data
    await storeReviews.fetchReviewById(reviewId)

    // Export the large plain blob before navigating — one quick canvas op (~50ms)
    // Must happen while the editor is still mounted and imageRef is alive
    let sharePlainBlob = null
    if (editor) {
      try {
        sharePlainBlob = await editor.exportImage(1200, 900, 'plain')
      } catch (e) {
        console.warn('Quick plain export failed:', e)
      }
    }

    // Store the created review data for the success sheet
    createdReview.value = {
      ...review,
      id: reviewId,
      score_overall: parseFloat(scoreOverallComputed.value)
    }

    // Navigate to review detail page immediately
    router.push({ name: 'WebReviewsSingle', params: { id: reviewId } })

    // Trigger celebration
    haptics.success()
    formBusy.value = false
    triggerCelebration()

    // Swap directly to the share sheet — openModal handles the close/reopen
    // cycle internally (works on both mobile Ionic modals and desktop simple modals)
    storeModals.openModal({
      type: 'TitleShare',
      props: {
        review: createdReview.value,
        title: selectedTMDBTitle.value,
        imageBlobs: sharePlainBlob ? { plain: sharePlainBlob } : null
      },
      canDismiss: true
    })

    // Export all image sizes and upload in the background (fire and forget)
    if (reviewId && editor) {
      const uploadVariantSizes = async (blobs, variant) => {
        if (!blobs) return

        for (const [sizeName, blob] of Object.entries(blobs)) {
          if (!blob) continue

          try {
            const variantWithSize =
              sizeName === 'large' ? variant : `${variant}_${sizeName}`
            await storeReviews.uploadReviewImage(
              reviewId,
              blob,
              variantWithSize,
              transformData
            )
          } catch (uploadError) {
            console.error(
              `Failed to upload ${variant} (${sizeName}):`,
              uploadError
            )
          }
        }
      }

      const exportAndUploadAll = async () => {
        const plainImageBlobs = {}
        const withTextImageBlobs = {}

        const sizesToExport = [
          { name: 'large', width: 1200, height: 900 },
          { name: 'medium', width: 800, height: 600 },
          { name: 'small', width: 400, height: 300 },
          { name: 'xsmall', width: 200, height: 150 }
        ]

        for (const size of sizesToExport) {
          const [plain, withText] = await Promise.all([
            editor.exportImage(size.width, size.height, 'plain'),
            editor.exportImage(size.width, size.height, 'with_text')
          ])
          plainImageBlobs[size.name] = plain
          withTextImageBlobs[size.name] = withText
        }

        await Promise.all([
          uploadVariantSizes(plainImageBlobs, 'plain'),
          uploadVariantSizes(withTextImageBlobs, 'with_text')
        ])

        storeReviews.fetchReviewById(reviewId)
      }

      exportAndUploadAll().catch((err) => {
        console.error('Background image export/upload failed:', err)
      })
    }

    // Success sheet will handle navigation/close when user dismisses it
  } catch (error) {
    haptics.error()
    formError.value = error
    formErrorMessage.value = error?.message
    formBusy.value = false
  }
}

const clearFormError = () => {
  formError.value = null
}

onMounted(() => {
  if (props.existingReview) {
    setReviewForEdit(props.existingReview)
    // Show score color immediately when editing an existing review
    hasInteracted.value = true
  } else {
    setReviewForNew()

    // Only call selectTitleForReview if props.title is defined
    if (props.title) {
      selectTitleForReview(
        props.title,
        props.reviewType,
        props.tvSeason,
        props.tvEpisode
      )
    }
    // Draft creation is handled by the title watcher (immediate: true)
    // which fires createDraft() as soon as the title is available
  }
})

// Save draft progress when user closes the form (instead of deleting)
onBeforeUnmount(() => {
  if (draftId.value) {
    saveDraftProgress()
  }
})

const handleStartFresh = () => {
  haptics.light()
  hasInteracted.value = false
  discardAndRestart()
}

const handleOpenChildModal = (modal) => {
  storeModals.openChildModal({
    type: 'ReviewFormChild',
    props: { title: props.title, review: review }
  })
}
</script>
